import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productService, newsletterService } from '../services/api';

function Home() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    try {
      const data = await productService.index({ featured: 1, active_only: 1 });
      setFeaturedProducts(data.data || []);
    } catch (error) {
      console.error('Error loading featured products:', error);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await newsletterService.subscribe({ email });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      console.error('Subscription failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="text-center px-4">
        <h1 className="text-5xl font-bold text-gray-900 mb-4">Welcome to Ortus</h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Your comprehensive business management platform for products, orders, 
          inventory, and more.
        </p>
        <div className="flex justify-center space-x-4">
          <Link 
            to="/login" 
            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition duration-200 font-medium"
          >
            Login
          </Link>
          <Link 
            to="/register" 
            className="bg-white text-gray-800 px-8 py-3 rounded-lg hover:bg-gray-100 transition duration-200 font-medium border border-gray-300"
          >
            Register
          </Link>
        </div>

        {featuredProducts.length > 0 && (
          <div className="mt-16 max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Featured Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredProducts.map((product) => (
                <div key={product.id} className="bg-white p-4 rounded-lg shadow">
                  <div className="h-32 bg-gray-100 rounded mb-3 flex items-center justify-center text-gray-400 text-sm">Product</div>
                  <h3 className="font-semibold">{product.name}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2">{product.description}</p>
                  <div className="mt-2 font-bold">₹{product.discount_price || product.price}</div>
                  <Link to="/shop" className="mt-2 inline-block text-blue-600 text-sm">View in Shop</Link>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="text-blue-600 mb-4">
              <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Product Management</h3>
            <p className="text-gray-600 text-sm">Manage products, categories, and inventory</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="text-blue-600 mb-4">
              <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">Order Processing</h3>
            <p className="text-gray-600 text-sm">Track and manage orders efficiently</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="text-blue-600 mb-4">
              <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">User Management</h3>
            <p className="text-gray-600 text-sm">Administer users and permissions</p>
          </div>
        </div>

        <div className="mt-12 max-w-md mx-auto">
          {subscribed ? (
            <div className="bg-green-50 text-green-700 p-4 rounded">Thanks for subscribing!</div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                required
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 border rounded p-2"
              />
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Subscribe</button>
            </form>
          )}
        </div>

        <div className="mt-8 flex justify-center gap-6 text-sm text-gray-600">
          <Link to="/page/about-us-6a040bb2a6d35">About Us</Link>
          <Link to="/shop">Shop</Link>
          <Link to="/dealers">Dealers</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/careers">Careers</Link>
          <Link to="/distributors">Distributors</Link>
          <Link to="/investments">Investments</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/login">Login</Link>
        </div>
      </div>
    </div>
  );
}

export default Home;
